import { inject, Inject, Injectable } from '@angular/core';
import { from, Observable, of, switchMap } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { UrlService } from './url.service';
import { OidcStorageService } from '../storage/oidc-storage.service';
import { LoggerFactory } from '../../shared/providers/logger/logger-factory.service';
import { LoggerWrapper } from '../../shared/providers/logger/logger-wrapper.service';
import { CallbackContext } from './callback-context';
import { CodeFlowCallbackHandlerService } from './callback-handling/code-flow-callback-handler.service';
import { UserCallbackHandlerService } from './callback-handling/user-callback-handler.service';
import { StateValidationCallbackHandlerService } from './callback-handling/state-validation-callback-handler.service';
import { LoginResponse, OpenIdConfiguration } from '../oidc-security.service';

@Injectable({ providedIn: 'root' })
export class CheckAuthService {
  private logger: LoggerWrapper = inject(LoggerFactory).getInstance(this);

  constructor(
    private readonly urlService: UrlService,
    private readonly oidcStorageService: OidcStorageService,
    private readonly codeFlowCallbackHandlerService: CodeFlowCallbackHandlerService,
    private readonly userHandlerService: UserCallbackHandlerService,
    private readonly stateValidationCallbackHandlerService: StateValidationCallbackHandlerService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  checkAuth(config: OpenIdConfiguration, url?: string): Observable<LoginResponse> {
    const currentUrl = url || this.getCurrentUrl();
    const isCallback = this.urlService.isACallbackUrl(currentUrl);

    const callback$ = isCallback ? this.exchangeCodeForTokens(currentUrl, config) : of(null);

    return callback$.pipe(
      switchMap(() => {
        return from(
          Promise.all([
            this.oidcStorageService.getUserDataFromStore(config.type),
            this.oidcStorageService.getAccessToken(config.type),
            this.oidcStorageService.getIdToken(config.type),
            this.oidcStorageService.getRefreshToken(config.type),
          ])
        ).pipe(
          map(([userData, accessToken, idToken, refreshToken]) => {
            const sessionExistInStorage = !!userData && !!accessToken && !!idToken && !!refreshToken;
            return {
              isAuthenticated: sessionExistInStorage,
              userData,
              accessToken,
              idToken,
              type: config.type,
            } as LoginResponse;
          })
        );
      }),
      catchError((err) => {
        const { message } = err;
        this.logger.error(err, '[oidc] checkAuth');
        return of({
          isAuthenticated: false,
          errorMessage: message,
          userData: null,
          idToken: '',
          accessToken: '',
          type: config.type,
        });
      })
    );
  }

  private exchangeCodeForTokens(urlToCheck: string, config: OpenIdConfiguration): Observable<CallbackContext> {
    return this.codeFlowCallbackHandlerService.extractCodeAndStateFromURL(urlToCheck).pipe(
      concatMap((callbackContext) =>
        this.codeFlowCallbackHandlerService.validateStateAndFetchTokensFromCode(callbackContext, config)
      ),
      concatMap((callbackContext) =>
        this.stateValidationCallbackHandlerService.validateAndSaveSessionTokens(callbackContext, config)
      ),
      concatMap((callbackContext) => this.userHandlerService.saveUserData(callbackContext, config))
    );
  }

  private getCurrentUrl(): string {
    return this.document.defaultView.location.toString();
  }
}
